import { Component, OnDestroy } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { PanelContainerComponent } from '../../../shared/content-panels/panel-container.component';
import { PanelBodyComponent } from 'src/app/shared/content-panels';
import { MatStepperModule } from '@angular/material/stepper';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { ItemListPanelComponent } from 'src/app/shared/item-list-panel/item-list-panel.component';
import { Observable, Subject, takeUntil } from 'rxjs';
import { QBuiltService } from '../services/qbuilt-service';
import { ChecklistValues } from '../models/checklist.model';
import {
  SaveStationModel,
  SaveTaskModel,
  TaskGroupModel,
} from 'src/app/shared/models/station.model';
import { ActivatedRoute } from '@angular/router';
import { QBuiltConstants } from '../models/constants/qbuilt.constants';
import { localStorageConstants, SpinnerService } from 'src/app/shared/services';
import { HbgSnackBar } from 'src/app/shared/toaster/hbg-toaster';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { CanDeactivateWithUnsavedChanges } from 'src/app/shared/guards/unsaved-changes.guard';

@Component({
  selector: 'new-edit-checklist',
  imports: [
    MatIcon,
    PanelContainerComponent,
    PanelBodyComponent,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    ItemListPanelComponent,
    MatTooltipModule,
  ],
  templateUrl: './new-edit-checklist.component.html',
  styleUrl: './new-edit-checklist.component.scss',
})
export class NewEditChecklistComponent
  implements CanDeactivateWithUnsavedChanges, OnDestroy
{
  panelType: number;
  stepperIndex: number;

  allRequired: boolean = false;

  //button text
  backButtonText: string = this.qbuiltConstants.backButtonText;
  saveButtonText: string = this.qbuiltConstants.saveButtonText;
  cancelButtonText: string = this.qbuiltConstants.cancelButtonText;
  nextButtonText: string = this.qbuiltConstants.nextButtonText;

  //first form group - checklist name and comments
  firstFormGroup = this._formBuilder.group({
    checklistName: [''],
    comments: [''],
  });

  //second form group - stationList with all items inside
  secondFormGroup = this._formBuilder.group({
    stationList: [''],
  });

  allTasks: any[] = [];
  private facilityNumber: string;

  isEditMode: boolean = false;
  checklistId: string;

  saveTrigger: boolean = false;

  onDestroy$: Subject<void> = new Subject<void>();

  changesMade: boolean = false;

  initialFirstFormValue: string;
  initialSecondFormValue: string;

  constructor(
    private _formBuilder: FormBuilder,
    private router: Router,
    private qbuiltService: QBuiltService,
    private activatedRoute: ActivatedRoute,
    public qbuiltConstants: QBuiltConstants,
    private spinnerService: SpinnerService,
    private toaster: HbgSnackBar,
  ) {
    this.qbuiltService.allRequiredItems$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((status: boolean) => {
        this.allRequired = status;
      });
  }

  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;

  ngOnInit() {
    this.spinnerService.show();
    this.qbuiltService.setFacilityNumber(
      localStorageConstants.USER_FACILITIES_KEY,
    );
    this.qbuiltService.facilityNumber$.subscribe((number) => {
      this.facilityNumber = number;
    });
    if (!this.facilityNumber) {
      this.spinnerService.hide();
      this.toaster.stickyError(`Facility number is not set. Please select a facility before creating or editing a checklist.`);
      this.router.navigate(['cqms']);
      return;
    }

    // Load saved step index from session storage
    const savedStepIndex = sessionStorage.getItem('stepperStepIndex');
    if (savedStepIndex !== null) {
      this.updateStepperData(savedStepIndex);
    }

    this.allRequired =
      sessionStorage.getItem('allRequired') === 'true' ? true : false;

    //check for edit mode
    this.activatedRoute.params.subscribe((params) => {
      this.checklistId = params['id'];
      sessionStorage.setItem('checklistId', this.checklistId);

      this.isEditMode = !!this.checklistId;
    });

    //capture initial form value after setup
    this.initialFirstFormValue = JSON.stringify(
      this.firstFormGroup.getRawValue(),
    );
    this.initialSecondFormValue = JSON.stringify(
      this.secondFormGroup.getRawValue(),
    );
    this.changesMade = false;

    //looks for changes to checklist name to preserve data
    this.firstFormGroup.valueChanges.subscribe((val) => {
      const currentFirstFormValue = JSON.stringify(
        this.firstFormGroup.getRawValue(),
      );
      this.changesMade =
        currentFirstFormValue !== this.initialFirstFormValue ||
        JSON.stringify(this.secondFormGroup.getRawValue()) !==
          this.initialSecondFormValue;
      if (this.changesMade) {
        sessionStorage.setItem('firstFormGroup', JSON.stringify(val));
      }
    });

    this.secondFormGroup.valueChanges.subscribe((val) => {
      const currentSecondFormValue = JSON.stringify(
        this.secondFormGroup.getRawValue(),
      );
      this.changesMade =
        currentSecondFormValue !== this.initialSecondFormValue ||
        JSON.stringify(this.firstFormGroup.getRawValue()) !==
          this.initialFirstFormValue;
      if (this.changesMade) {
        sessionStorage.setItem('secondFormGroup', JSON.stringify(val));
      }
    });

    //if edit mode pull data from session storage otherwise forms are blank
    const firstFormGroupData = sessionStorage.getItem('firstFormGroup');
    const secondFormGroupData = sessionStorage.getItem('secondFormGroup');

    if (firstFormGroupData || secondFormGroupData) {
      this.firstFormGroup.setValue(JSON.parse(firstFormGroupData), {
        emitEvent: false,
      });
      this.secondFormGroup.setValue(JSON.parse(secondFormGroupData), {
        emitEvent: false,
      });
      // Re-snapshot so dirty-tracking is relative to the restored state, not the blank form
      this.initialFirstFormValue = JSON.stringify(
        this.firstFormGroup.getRawValue(),
      );
      this.initialSecondFormValue = JSON.stringify(
        this.secondFormGroup.getRawValue(),
      );
      this.spinnerService.hide();
    } else {
      this.spinnerService.hide();
    }
  }

  /**
   * prevents save if user has created an empty station
   */
  emptyStation() {
    const stationList =
      JSON.parse(sessionStorage.getItem('secondFormGroup') || '[]')
        .stationList || [];
    return stationList.some(
      (station: any) => !station.taskList || station.taskList.length === 0,
    );
  }

  ngOnDestroy() {
    this.clearStepperData();
    sessionStorage.removeItem('allRequired');
    sessionStorage.removeItem('checklistId');
    sessionStorage.removeItem('requiredTaskIds');
    sessionStorage.removeItem('optionalTaskIds');
    sessionStorage.removeItem('firstFormGroup');
    sessionStorage.removeItem('secondFormGroup');
    sessionStorage.removeItem('stepperStepIndex');
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  // Implementation for CanComponentDeactivate interface
  hasUnsavedChanges(): boolean {
    return this.changesMade;
  }

  getSaveChangesDialogConfig() {
    return {
      title: this.qbuiltConstants.dialogTitle,
      message: this.qbuiltConstants.dialogMessage,
      cancelButtonText: this.qbuiltConstants.cancelButtonText,
      confirmButtonText: this.qbuiltConstants.confirmButtonText,
    };
  }

  isSaveInProgress(): boolean {
    return this.saveTrigger;
  }

  /**
   * Catches changes to the stepper to use for tracking updates
   * and maintaining data
   * @param event
   */
  onStepperChange(event: StepperSelectionEvent) {
    sessionStorage.setItem('stepperStepIndex', event.selectedIndex.toString());
    sessionStorage.setItem(
      'firstFormGroup',
      JSON.stringify(this.firstFormGroup.value),
    );
    sessionStorage.setItem(
      'secondFormGroup',
      JSON.stringify(this.secondFormGroup.value),
    );
  }

  /** maintains the page and data for user incase of page refresh */
  updateStepperData(savedStepIndex: any) {
    this.stepperIndex = +savedStepIndex;
    const firstFormGroupData = sessionStorage.getItem('firstFormGroup');
    const secondFormGroupData = sessionStorage.getItem('secondFormGroup');

    this.firstFormGroup.setValue(JSON.parse(firstFormGroupData));
    this.secondFormGroup.setValue(JSON.parse(secondFormGroupData));
  }

  clearStepperData() {
    sessionStorage.removeItem('stepperStepIndex');
    sessionStorage.removeItem('firstFormGroup');
    sessionStorage.removeItem('secondFormGroup');
  }

  //checks for empty station to prevent saving checklist
  emptyStationCheck() {
    let stationList =
      JSON.parse(sessionStorage.getItem('secondFormGroup') || '[]')
        .stationList || [];
    return stationList.some(
      (station: any) => station.taskGroup[0].taskList.length === 0,
    );
  }

  /**
   * Captures updates from child component on required item panel
   * for both station changes and task item changes
   * @param event
   */
  onStationUpdate(event: any) {
    this.secondFormGroup.patchValue({
      stationList: event,
    });
    sessionStorage.setItem(
      'secondFormGroup',
      JSON.stringify(this.secondFormGroup.value),
    );
  }

  disableSave() {
    if (
      !this.firstFormGroup.valid ||
      !this.allRequired ||
      this.emptyStationCheck()
    ) {
      return true;
    } else {
      return false;
    }
  }

  saveForm(first: FormGroup, second: FormGroup) {
    this.saveTrigger = true;
    this.spinnerService.show();
    let EditListId = parseInt(this.checklistId);
    let finalChecklist: ChecklistValues = {
      Comment: first.value.comments,
      Id: this.isEditMode ? EditListId : 0,
      Name: first.value.checklistName,
      Stations: this.formatStations(second.value.stationList),
    };
    this.qbuiltService.saveChecklist(finalChecklist).subscribe({
      next: (result) => {
        if (!result.Success) {
          this.spinnerService.hide();
          this.toaster.stickyError(
            `${result.Message} - ${new Date().toLocaleString()}`,
          );
          console.log('Error saving checklist:', result.Message);
          return;
        }
        this.spinnerService.hide();
        this.onDestroy$.next();
        this.onDestroy$.complete();
        this.toaster.open('Checklist saved successfully!');
        this.router.navigate(['cqms']);
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toaster.stickyError(
          `Failed to save checklist! ${new Date().toLocaleString()}`,
        );
        console.log('Error saving checklist:', err);
      },
    });
  }

  //Formats station data to match SaveStationModel
  formatStations(stationList: any) {
    let saveStations: SaveStationModel[] = [];
    stationList.forEach((station: any) => {
      saveStations.push({
        Name: station.name,
        HBGFacilityNumber: this.facilityNumber,
        Id: typeof station.id === 'number' ? station.id : 0,
        TaskGroups: this.formatTaskGroups([station]),
      });
    });
    return saveStations;
  }

  //formats task groups to match TaskGroupModel
  formatTaskGroups(stationList: any) {
    let saveTaskGroup: TaskGroupModel[] = [];
    stationList.forEach((station: any) => {
      station.taskGroup.forEach((group: any) => {
        saveTaskGroup.push({
          Name: group.name,
          Id: typeof group.id === 'number' ? group.id : 0,
          Tasks: this.formatTasks(group.taskList),
        });
      });
    });
    return saveTaskGroup;
  }

  formatTasks(taskGroup: any) {
    let saveTasks: SaveTaskModel[] = [];
    taskGroup.forEach((task: any) => {
      let required = task.taskType === 'Required' ? true : false;
      saveTasks.push({
        Id: task.taskId,
        Name: task.taskName,
        IsRequired: required,
        OrderOfPresentation: taskGroup.indexOf(task) + 1, // Add 1 to make it 1-based index
      });
    });
    return saveTasks;
  }

  onCancel() {
    this.router.navigate(['cqms']);
  }
}
